import { Component, OnInit, Inject, ChangeDetectorRef, ViewRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StaticDataService } from 'src/app/services/static-data.service';
import { DoctorDataModel } from 'src/app/models/doctors/doctor-data.model';
import { ClientLocationDataModel } from 'src/app/models/clients/client-location-data.model';
import { TdoClientConfigurationModel } from 'src/app/models/clients/tdo-client-configuration.model';
import { OfficeLocationsService } from 'src/app/modules/configuration/office-locations/office-locations.service';
import { DoctorsService } from 'src/app/services/doctors.service';
import { Globals } from 'src/app/globals';
import { ARService } from 'src/app/modules/ar/ar.service';
import { ARPatientInterestModel } from 'src/app/models/accountsReceivable/Interest-Late-Fee.model';
import { UiService } from 'src/app/services/ui.service';

interface PatientAccount {
  patientNumber: string;
  patientName: string;
  practice: string;
  total: number;
  interestPercent: number;
  interestAmount: number;
  lateFeeAmount: number;
  selected: boolean;
  agingBuckets: {
    thirtyDay: number;
    sixtyDay: number;
    ninetyDay: number;
  };
}

interface Practice {
  id: string;
  name: string;
  selected: boolean;
}
interface Doctor {
  id: string;
  name: string;
}

@Component({
  standalone: false,
  selector: 'app-interest-and-late-fees',
  templateUrl: './interest-and-late-fees.component.html',
  styleUrls: ['./interest-and-late-fees.component.scss']
})
export class InterestAndLateFeesComponent implements OnInit {
  
  // Global controls
  globalInterestRate: number = 0;
  globalLateFeeAmount: number = 0;
  selectAll: boolean = true;
  
  // API response messages
  apiMessage: string = '';
  apiMessageType: 'success' | 'error' | '' = '';
  
  // Filtering options
  doctors: Doctor[] = [];
  selectedDoctor: Doctor | null = null;
  accountStatus: string = '30'; // Default to 30 Day over and older
  
  // Practice and Location selections (matching report-aging-filter)
  clientList: TdoClientConfigurationModel[] = [];
  locationList: ClientLocationDataModel[] = [];
  selectedClient: TdoClientConfigurationModel;
  selectedClientLocation: ClientLocationDataModel | null = null;
  isReportForPractice: boolean = true;
  
  // Patient accounts data
  patientAccounts: (ARPatientInterestModel & { selected: boolean })[] = [];
   
  
  isLoading = true;

  constructor(
    private dialogRef: MatDialogRef<InterestAndLateFeesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private staticDataService: StaticDataService,
    private locService: OfficeLocationsService,
    private doctorService: DoctorsService,
    private globals: Globals,
    private arService: ARService,
    private readonly uiService: UiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeDoctors();
    this.loadServiceData();
    this.isLoading = false;
  }

  private initializeDoctors(): void {
    // Check if doctors are already loaded
    if (this.staticDataService.currentLocationDoctors && this.staticDataService.currentLocationDoctors.length > 0) {
      this.setupDoctors();
    } else {
      // If not loaded, get them from service
      this.staticDataService.getCurrentLocationDoctors();
      
      // Wait for doctors to be loaded
      const checkDoctors = setInterval(() => {
        if (this.staticDataService.currentLocationDoctors && this.staticDataService.currentLocationDoctors.length > 0) {
          clearInterval(checkDoctors);
          this.setupDoctors();
        }
      }, 100);
    }
  }

  // Same data loading functionality as report-aging-filter
  private loadServiceData(): void {
    this.locService.getClients().subscribe(data => {
      this.clientList = data;
      if (this.clientList.length > 0) {
        // Try to locate current user's client
        this.selectedClient = this.clientList.find(p => p.TDOCID.toString() === this.globals.TDOCID);
        if (this.selectedClient) {
          // Get locations for current practice
          this.doctorService.getClientLocations(this.selectedClient.TDOCID.toString()).subscribe(ldata => {
            this.locationList = ldata;
            if (this.locationList.length > 0) {
              const currentLocation = this.locationList.find(p => p.LOCID.toString() === this.globals.LOCID);
              if (currentLocation) {
                this.selectedClientLocation = currentLocation;
              } else {
                this.selectedClientLocation = this.locationList[0];
              }
            }
          });
        } else {
          // Fallback to first client if current user's client not found
          this.selectedClient = this.clientList[0];
          this.loadLocationsForClient(this.selectedClient.TDOCID);
        }
      }
    }, err => {
      console.log('Error loading clients:', err);
    });
  }

  private loadLocationsForClient(tdocId: number): void {
    this.doctorService.getClientLocations(tdocId.toString()).subscribe(ldata => {
      this.locationList = ldata;
      if (this.locationList.length > 0) {
        const currentLocation = this.locationList.find(p => p.LOCID.toString() === this.globals.LOCID);
        if (currentLocation) {
          this.selectedClientLocation = currentLocation;
        } else {
          this.selectedClientLocation = this.locationList[0];
        }
      }
    }, err => {
      console.log('Error loading locations:', err);
    });
  }

  private setupDoctors(): void {
    if (this.staticDataService.currentLocationDoctors) {
      this.doctors = this.staticDataService.currentLocationDoctors.map((doctor: DoctorDataModel) => ({
        id: doctor.DoctorId,
        name: this.getDoctorDisplayName(doctor)
      }));
      // Set first doctor as default selection
      if (this.doctors.length > 0) {
        this.selectedDoctor = this.doctors[0];
      }
    }
  }

  private getDoctorDisplayName(doctor: DoctorDataModel): string {
    if (doctor?.dataContact) {
      return `Dr. ${doctor.dataContact.FirstName} ${doctor.dataContact.LastName}`;
    }
    return 'Unknown Doctor';
  }

  onDoctorChange(doctor: Doctor): void {
    if (doctor && doctor.id) {
      this.selectedDoctor = doctor;
      // Clear patient accounts when doctor changes
      this.patientAccounts = [];
    } else {
      this.selectedDoctor = null;
      this.patientAccounts = [];
    }
  }

  onAccountStatusChange(status: string): void {
    if (status) {
      this.accountStatus = status;
      // Clear patient accounts when account status changes
      this.patientAccounts = [];
    } else {
      this.accountStatus = '';
      this.patientAccounts = [];
    }
  }

  // Practice and Location change handlers (matching report-aging-filter)
  onChangeClient(client: any): void {
    this.selectedClient = client.value;
    // Load locations for the selected client
    this.loadLocationsForClient(client.value.TDOCID);
    // Clear previous location selection
    this.selectedClientLocation = null;
    // Clear patient accounts when client changes
    this.patientAccounts = [];
  }

  onChangeClientLocation(locations: any): void {
    if (locations && locations.value && locations.value.LOCID) {
      this.selectedClientLocation = locations.value;
      // Clear patient accounts when location changes
      this.patientAccounts = [];
    } else {
      // Clear location selection if no valid selection
      this.selectedClientLocation = null;
      // No valid location selected, clearing selection
      // Clear patient accounts when location is cleared
      this.patientAccounts = [];
    }
  }

  updateFilterByPractice(isChecked: boolean): void {
    this.isReportForPractice = isChecked;
    if (!isChecked) {
      this.selectedClient = null;
      this.selectedClientLocation = null;
    }
  }

    canShowList(): boolean {
    // Check if all required selections are made
    const hasLocation = !!this.selectedClientLocation;
    const hasDoctor = !!this.selectedDoctor;
    const hasAccountStatus = !!this.accountStatus;
    
    /* console.log('Validation check:', {
      hasLocation,
      hasDoctor,
      hasAccountStatus,
      selectedClientLocation: this.selectedClientLocation,
      selectedDoctor: this.selectedDoctor,
      accountStatus: this.accountStatus
    });*/
    
    return hasLocation && hasDoctor && hasAccountStatus;
  }

  canApplyGlobalRates(): boolean {
    // Check if we have patients loaded and at least one is selected
    const hasPatients = this.patientAccounts && this.patientAccounts.length > 0;
    const hasSelectedPatients = this.patientAccounts && this.patientAccounts.some(account => account.selected);
    
    return hasPatients && hasSelectedPatients;
  }

  canSubmitCharges(): boolean {
    // Check if we have patients loaded and at least one is selected
    const hasPatients = this.patientAccounts && this.patientAccounts.length > 0;
    const hasSelectedPatients = this.patientAccounts && this.patientAccounts.some(account => account.selected);
    
    return hasPatients && hasSelectedPatients;
  }

  onShowList(): void {
    // Apply filters and show patient list
    /*console.log('Filters applied:', {
      practices: this.clientList,
      selectedDoctor: this.selectedDoctor,
      accountStatus: this.accountStatus,
      selectedClient: this.selectedClient,
      selectedClientLocation: this.selectedClientLocation
    });*/

    // Prepare parameters for API call
    const locationIds = this.selectedClientLocation?.LOCID?.toString() || '';
    const doctorIds = this.selectedDoctor?.id || '';
    
    // Clear previous results and show spinner while loading
    this.patientAccounts = [];
    this.isLoading = true;

    // Call API to get AR list patients
    this.arService.getARListPatients(locationIds, doctorIds, this.accountStatus, this.selectedClient.TDOCID).subscribe(
      (response) => {
        //console.log('AR List Patients response:', response);
        this.patientAccounts = response.map(patient => ({
          ...patient,
          selected: true,
          InterestP: patient.InterestP || 0,
          InterestA: patient.InterestA || 0,
          LateFee: patient.LateFee || 0
        }));
        this.selectAll = true;
        this.isLoading = false;
        if (!(this.cdr as ViewRef).destroyed) { this.cdr.detectChanges(); }

       // console.log('Mapped patient accounts:', this.patientAccounts);
        // Handle the response here - you can update patientAccounts or handle as needed
      },
      (error) => {
        console.error('Error getting AR List Patients:', error);
        this.isLoading = false;
        if (!(this.cdr as ViewRef).destroyed) { this.cdr.detectChanges(); }
        this.createMessage('Error loading patients. Please try again.', 'Error!');
      }
    );
  }

  onApplyGlobalRates(): void {
    // Apply global interest rate and late fee to all selected patients
    this.patientAccounts.forEach(account => {
      if (account.selected) {
        account.InterestP = this.globalInterestRate;
        account.InterestA = (account.Total * this.globalInterestRate) / 100;
        account.LateFee = this.globalLateFeeAmount;
        // Don't update Total here as it will be calculated when individual fields lose focus
      }
    });

    // Update all selected accounts in the backend
    const selectedAccounts = this.patientAccounts.filter(account => account.selected);
    
    if (selectedAccounts.length > 0) {
      this.isLoading = true;
      
      this.arService.updatePatientInterestAndLateFees(selectedAccounts).subscribe(
        (response) => {
          this.isLoading = false;
          if (!(this.cdr as ViewRef).destroyed) { this.cdr.detectChanges(); }
          //this.apiMessage = '';
          this.createMessage('Global rates applied successfully!', 'Success!');
          //this.apiMessageType = 'success';
          
          // Clear message after 3 seconds
          setTimeout(() => {
            this.apiMessage = '';
            this.apiMessageType = '';
          }, 3000);
        },
        (error) => {
          this.isLoading = false;
          if (!(this.cdr as ViewRef).destroyed) { this.cdr.detectChanges(); }
          //  this.apiMessage = 'Error applying global rates. Please try again.';
          this.createMessage('Error applying global rates. Please try again.', 'Error!');
          //this.apiMessageType = 'error';
          
          // Clear message after 3 seconds
          setTimeout(() => {
            this.apiMessage = '';
            this.apiMessageType = '';
          }, 3000);
        }
      );
    }
  }

  createMessage(message, type) {
    this.uiService.displayMessage(type, message);
}

  onSelectAllChange(): void {
    this.patientAccounts.forEach(account => {
      account.selected = this.selectAll;
    });
  }

  onPatientSelectionChange(): void {
    // Update select all checkbox based on individual selections
    this.selectAll = this.patientAccounts.every(account => account.selected);
  }

  onSubmitCharges(): void {
    if (this.patientAccounts.some(account => account.selected)) {
      this.isLoading = true;
      
      // Process selected accounts
      const selectedAccounts = this.patientAccounts.filter(account => account.selected);
      
      // Call API to submit interest and late fee charges
      selectedAccounts.forEach(x => x.localInterestFeeDate = new Date());
      this.arService.submitInterestAndLateFeeCharges(selectedAccounts).subscribe(
        (response) => {
          this.isLoading = false;
          if (!(this.cdr as ViewRef).destroyed) { this.cdr.detectChanges(); }

          // Show success message
          this.createMessage('Charges submitted successfully!', 'Success!');
          
          // Close dialog with success response
          this.dialogRef.close({
            action: 'submit',
            success: true,
            response: response,
            accounts: selectedAccounts,
            globalRates: {
              interestRate: this.globalInterestRate,
              lateFeeAmount: this.globalLateFeeAmount
            },
            filters: {
              selectedClient: this.selectedClient,
              selectedClientLocation: this.selectedClientLocation
            }
          });
        },
        (error) => {
          this.isLoading = false;
          if (!(this.cdr as ViewRef).destroyed) { this.cdr.detectChanges(); }

          // Show error message
          this.createMessage('Error submitting charges. Please try again.', 'Error!');
          
          // Don't close dialog on error, let user try again
        }
      );
    } else {
      // No accounts selected
      this.createMessage('Please select at least one patient account to submit charges.', 'Warning!');
    }
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  calculateInterest(account: ARPatientInterestModel & { selected: boolean }): void {
    account.InterestA = (account.Total * account.InterestP) / 100;
  }

  onInterestPercentageBlur(account: ARPatientInterestModel & { selected: boolean }): void {
    // Calculate interest amount when interest percentage loses focus
    account.InterestA = (account.Total * account.InterestP) / 100;
    
    // Update the account in the backend
    this.updateSingleAccount(account);
  }

  onInterestPercentageChange(account: ARPatientInterestModel & { selected: boolean }): void {
    // Calculate interest amount in real-time when typing (without calling API)
    account.InterestA = (account.Total * account.InterestP) / 100;
  }

  onLateFeeBlur(account: ARPatientInterestModel & { selected: boolean }): void {
    // Update the account in the backend when late fee loses focus
    this.updateSingleAccount(account);
  }

  private updateSingleAccount(account: ARPatientInterestModel & { selected: boolean }): void {
    if (account && account.PatientId) {
      this.isLoading = true;
      
      this.arService.updatePatientInterestAndLateFees([account]).subscribe(
        (response) => {
          this.isLoading = false;
          if (!(this.cdr as ViewRef).destroyed) { this.cdr.detectChanges(); }
          //this.apiMessage = 'Account updated successfully!';
          this.createMessage('Account updated successfully!', 'Success!');
          //this.apiMessageType = 'success';
          
          // Clear message after 3 seconds
          setTimeout(() => {
            this.apiMessage = '';
            this.apiMessageType = '';
          }, 3000);
        },
        (error) => {
          this.isLoading = false;
          if (!(this.cdr as ViewRef).destroyed) { this.cdr.detectChanges(); }
          //this.apiMessage = 'Error updating account. Please try again.';
          this.createMessage('Error updating account. Please try again.', 'Error!');
          //this.apiMessageType = 'error';
          
          // Clear message after 3 seconds
          setTimeout(() => {
            this.apiMessage = '';
            this.apiMessageType = '';
          }, 3000);
        }
      );
    }
  }

  calculateLateFees(account: PatientAccount): void {
    // Late fee calculation logic here
    // Assign late fee if any aging bucket is non-zero
    const hasOverdue = account.agingBuckets.thirtyDay > 0 ||
                       account.agingBuckets.sixtyDay > 0 ||
                       account.agingBuckets.ninetyDay > 0;
    account.lateFeeAmount = hasOverdue ? this.globalLateFeeAmount : 0;
  }
}
